import React, { useEffect, useId, useRef, useState } from 'react';
import { ListItem } from '@cko/primitives';
import { IconActionAccordionChevronOpen } from '@cko/icons';

import { getMenuIcon } from '../../../../utils';
import { useSideNavMenu } from '../SideNavMenuProvider';
import { SideNavLevelTwoGrouping } from '../SideNavLevelTwoGrouping';
import { LevelOneText, StyledIconContainer } from '../SideNavMenu.styles';
import { SideNavLabel } from '../SideNavLabel';
import { AvailableGroupMenuItem } from '../../../../providers/NavigationProvider/types';
import { SideNavMenuItemLink } from '../SideNavMenuItemLink';
import { StyledAccordionBody, StyledMenuAccordionButton } from './SideNavLevelOneGrouping.styles';

type SideNavLevelOneGroupingProps = {
  menuItemConfig: AvailableGroupMenuItem;
};

export const SideNavLevelOneGrouping = ({ menuItemConfig }: SideNavLevelOneGroupingProps) => {
  const { availableChildItems, iconName, label, statusLabel } = menuItemConfig;

  const IconComponent = getMenuIcon(iconName);

  const { activeGrouping, setActiveGrouping } = useSideNavMenu();
  const accordionBodyId = useId();
  const accordionButtonId = useId();
  const accordionBodyRef = useRef<HTMLUListElement>(null);

  const [maxHeight, setMaxHeight] = useState<number>(0);

  useEffect(() => {
    setMaxHeight(accordionBodyRef.current?.scrollHeight || 0);
  }, []);

  const handleClick = () => {
    if (activeGrouping === label) {
      setActiveGrouping(undefined);
      return;
    }

    setActiveGrouping(label);
  };

  const isOpen = activeGrouping === label;

  return (
    <ListItem>
      <StyledMenuAccordionButton
        onClick={handleClick}
        aria-controls={accordionBodyId}
        aria-expanded={isOpen}
        id={accordionButtonId}
      >
        {IconComponent && (
          <StyledIconContainer data-testid="menu-icon">
            <IconComponent />
          </StyledIconContainer>
        )}
        <LevelOneText>
          <SideNavLabel label={label} statusLabel={statusLabel} />
        </LevelOneText>
        {isOpen ? (
          <IconActionAccordionChevronOpen transform="rotate(180)" />
        ) : (
          <IconActionAccordionChevronOpen />
        )}
      </StyledMenuAccordionButton>
      <StyledAccordionBody
        id={accordionBodyId}
        ref={accordionBodyRef}
        role="region"
        aria-labelledby={accordionButtonId}
        isOpen={isOpen}
        bodyHeight={maxHeight}
      >
        {availableChildItems?.map((childItem) => {
          if (childItem.type === 'group') {
            return (
              <SideNavLevelTwoGrouping
                key={childItem.label}
                menuItemConfig={childItem}
                preventFocus={!isOpen}
              />
            );
          }

          if (childItem.type === 'internal_link') {
            return (
              <SideNavMenuItemLink
                key={childItem.routeId}
                menuItemConfig={childItem}
                level={2}
                preventFocus={!isOpen}
              />
            );
          }

          return null;
        })}
      </StyledAccordionBody>
    </ListItem>
  );
};
